import React, { useState, useEffect } from 'react';
import { Message as MessageType, MessageThinkingState } from '../../types';
import Message from './Message';
import ThinkingIndicator from './ThinkingIndicator';
import MessageThinking from './MessageThinking';
import EmptyConversation from './EmptyConversation';
import { useChat } from '../../contexts/ChatContext';

interface MessageListProps {
  messages: MessageType[];
  isLoading: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ messages, isLoading }) => {
  const { currentStage, stageMessage, progress, currentConversationId } = useChat();
  
  // Track thinking states for each message - load from localStorage or initialize empty
  const [thinkingStates, setThinkingStates] = useState<Map<string, MessageThinkingState>>(() => {
    if (currentConversationId) {
      const storageKey = `thinking_states_${currentConversationId}`;
      try {
        const storedStates = localStorage.getItem(storageKey);
        if (storedStates) {
          const parsedStates = JSON.parse(storedStates);
          const map = new Map<string, MessageThinkingState>();
          Object.entries(parsedStates).forEach(([key, state]: [string, any]) => {
            map.set(key, {
              ...state,
              trackedPlans: state.trackedPlans || [],
            });
          });
          return map;
        }
      } catch (error) {
        console.warn('Failed to load thinking states from localStorage:', error);
      }
    }
    return new Map();
  });
  const [activeThinkingMessageId, setActiveThinkingMessageId] = useState<string | null>(null);
  
  // Load thinking states when conversation changes
  useEffect(() => {
    if (currentConversationId) {
      const storageKey = `thinking_states_${currentConversationId}`;
      try {
        const storedStates = localStorage.getItem(storageKey);
        if (storedStates) {
          const parsedStates = JSON.parse(storedStates);
          const map = new Map<string, MessageThinkingState>();
          let restoredActiveId: string | null = null;
          Object.entries(parsedStates).forEach(([key, state]: [string, any]) => {
            map.set(key, {
              ...state,
              trackedPlans: state.trackedPlans || [],
            });
            if (state.isActive) {
              restoredActiveId = key;
            }
          });
          setThinkingStates(map);
          // FIX: Restore activeThinkingMessageId when returning to a conversation
          // that still has an active thinking process, instead of always resetting to null.
          if (restoredActiveId && isLoading) {
            setActiveThinkingMessageId(restoredActiveId);
          } else {
            setActiveThinkingMessageId(null);
          }
        } else {
          setThinkingStates(new Map());
          setActiveThinkingMessageId(null);
        }
      } catch (error) {
        console.warn('Failed to load thinking states from localStorage:', error);
        setThinkingStates(new Map());
        setActiveThinkingMessageId(null);
      }
    } else {
      setThinkingStates(new Map());
      setActiveThinkingMessageId(null);
    }
  }, [currentConversationId, isLoading]);
  
  // Save thinking states to conversation whenever they change
  useEffect(() => {
    if (currentConversationId && thinkingStates.size > 0) {
      const thinkingStatesObj: { [key: string]: MessageThinkingState } = {};
      thinkingStates.forEach((state, key) => {
        thinkingStatesObj[key] = {
          ...state,
          trackedPlans: state.trackedPlans,
        };
      });
      
      const storageKey = `thinking_states_${currentConversationId}`;
      try {
        localStorage.setItem(storageKey, JSON.stringify(thinkingStatesObj));
      } catch (error) {
        console.warn('Failed to save thinking states to localStorage:', error);
      }
    }
  }, [thinkingStates, currentConversationId]);
  
  // Get the last user message ID to associate thinking with
  const getLastUserMessageId = React.useCallback((): string | null => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        return messages[i].id;
      }
    }
    return null;
  }, [messages]);
  
  // Initialize thinking state when loading starts
  useEffect(() => {
    if (isLoading && !activeThinkingMessageId) {
      const lastUserMessageId = getLastUserMessageId();
      if (lastUserMessageId) {
        const thinkingId = `thinking-${lastUserMessageId}`;
        // FIX: Use functional updater so we check the LATEST pending state (not a stale closure).
        // If a state was just loaded from localStorage by the convChange effect, preserve it.
        setThinkingStates(prev => {
          const existing = prev.get(thinkingId);
          if (existing) {
            return new Map(prev).set(thinkingId, { ...existing, isActive: true });
          }
          const newThinkingState: MessageThinkingState = {
            messageId: thinkingId,
            userMessageId: lastUserMessageId,
            isActive: true,
            isExpanded: false,
            hasShownDetailedThinking: false,
            requirementHistory: [],
            trackedPlans: [],
          };
          return new Map(prev.set(thinkingId, newThinkingState));
        });
        setActiveThinkingMessageId(thinkingId);
      }
    }
  }, [isLoading, activeThinkingMessageId, getLastUserMessageId]);
  
  // Freeze active thinking state when loading completes
  useEffect(() => {
    if (!isLoading && activeThinkingMessageId) {
      setThinkingStates(prev => {
        const newMap = new Map(prev);
        const activeState = newMap.get(activeThinkingMessageId);
        if (activeState) {
          const frozenState: MessageThinkingState = {
            ...activeState,
            isActive: false,
            isExpanded: false,
            finalStage: currentStage,
            finalStageMessage: '',
            finalProgress: progress,
          };
          newMap.set(activeThinkingMessageId, frozenState);
        }
        return newMap;
      });
      setActiveThinkingMessageId(null);
    }
  }, [isLoading, activeThinkingMessageId, currentStage, progress]);
  
  // Auto-collapse when transitioning to citation processing and ensure detailed thinking is shown
  useEffect(() => {
    if (stageMessage?.includes('Adding citations') && activeThinkingMessageId) {
      setThinkingStates(prev => {
        const newMap = new Map(prev);
        const activeState = newMap.get(activeThinkingMessageId);
        if (activeState) {
          newMap.set(activeThinkingMessageId, { 
            ...activeState, 
            isExpanded: false,
            hasShownDetailedThinking: true
          });
        }
        return newMap;
      });
    }
  }, [stageMessage, activeThinkingMessageId]);
  
  // Reset thinking states when messages are cleared (new conversation)
  useEffect(() => {
    if (messages.length === 0) {
      setThinkingStates(new Map());
      setActiveThinkingMessageId(null);
    }
  }, [messages.length]);

  // Determine if we should show detailed thinking for the active state
  const shouldShowDetailedThinking = stageMessage && (
    stageMessage.includes('Plan:') ||
    stageMessage.includes('Gathering knowledge:') ||
    stageMessage.includes('Analyzing primary data:') ||
    stageMessage.includes('Analyzing enrichment data in parallel:') ||
    stageMessage.includes('Planning to fetch') ||
    stageMessage.includes('Creating consolidated results') ||
    stageMessage.includes('Deciding visualization configuration') ||
    stageMessage.includes('Generating final visualization') ||
    stageMessage.includes('Adding citations')
  );
  
  // Update active thinking state when detailed thinking should be shown
  useEffect(() => {
    if (shouldShowDetailedThinking && activeThinkingMessageId) {
      setThinkingStates(prev => {
        const newMap = new Map(prev);
        const activeState = newMap.get(activeThinkingMessageId);
        if (activeState && !activeState.hasShownDetailedThinking) {
          newMap.set(activeThinkingMessageId, {
            ...activeState,
            hasShownDetailedThinking: true,
            isExpanded: true,
          });
        }
        return newMap;
      });
    }
  }, [shouldShowDetailedThinking, activeThinkingMessageId]);
  
  // Update requirement history for active thinking state
  const handleUpdateRequirementHistory = (requirement: string) => {
    if (!activeThinkingMessageId) return;
    
    setThinkingStates(prev => {
      const newMap = new Map(prev);
      const activeState = newMap.get(activeThinkingMessageId);
      if (activeState && !activeState.requirementHistory.includes(requirement)) {
        newMap.set(activeThinkingMessageId, {
          ...activeState,
          requirementHistory: [...activeState.requirementHistory, requirement],
        });
      }
      return newMap;
    });
  };

  // Update tracked plans for active thinking state
  const handleUpdateTrackedPlans = (plan: string) => {
    if (!activeThinkingMessageId) return;
    
    setThinkingStates(prev => {
      const newMap = new Map(prev);
      const activeState = newMap.get(activeThinkingMessageId);
      if (activeState) {
        const updatedPlans = activeState.trackedPlans.includes(plan) 
          ? activeState.trackedPlans 
          : [...activeState.trackedPlans, plan];
        
        const updatedState = {
          ...activeState,
          trackedPlans: updatedPlans
        };
        newMap.set(activeThinkingMessageId, updatedState);
        
        const storageKey = `thinking_states_${currentConversationId}`;
        const thinkingStatesObj: { [key: string]: MessageThinkingState } = {};
        newMap.forEach((state, id) => {
          thinkingStatesObj[id] = state;
        });
        
        try {
          localStorage.setItem(storageKey, JSON.stringify(thinkingStatesObj));
        } catch (error) {
          console.warn('Failed to save thinking states to localStorage:', error);
        }
      }
      return newMap;
    });
  };
  
  // Handle expand/collapse for specific thinking state
  const handleToggleExpanded = (thinkingId: string, expanded: boolean) => {
    setThinkingStates(prev => {
      const newMap = new Map(prev);
      const state = newMap.get(thinkingId);
      if (state) {
        newMap.set(thinkingId, { ...state, isExpanded: expanded });
      }
      return newMap;
    });
  };
  
  // If no messages and not loading, show welcome
  if (messages.length === 0 && !isLoading) {
    return <EmptyConversation />;
  }
  return (
    <div className="space-y-6 py-4 w-full max-w-screen sm:max-w-sm md:max-w-xl lg:max-w-5xl mx-auto">
      {messages.map((message, index) => {
        // Find thinking state associated with this user message
        const associatedThinkingId = `thinking-${message.id}`;
        const thinkingState = thinkingStates.get(associatedThinkingId);
        const isActiveThinking = activeThinkingMessageId === associatedThinkingId;
        
        return (
          <React.Fragment key={message.id}>
            <Message message={message} />
            
            {/* Show thinking components after user messages that have associated thinking */}
            {message.role === 'user' && thinkingState && (
              <>
                {/* Show thinking indicator when this thinking is active and loading, but NOT during citations */}
                {isActiveThinking && isLoading && !stageMessage?.includes('Adding citations') && !thinkingState.hasShownDetailedThinking && (
                  <ThinkingIndicator 
                    currentStage={currentStage} 
                    stageMessage={stageMessage}
                    progress={progress}
                    hideText={!!shouldShowDetailedThinking}
                  />
                )}
                
                {/* Show detailed thinking widget if it has been shown for this message */}
                {thinkingState.hasShownDetailedThinking && (
                  <MessageThinking 
                    currentStage={thinkingState.isActive ? currentStage : thinkingState.finalStage}
                    stageMessage={
                      thinkingState.isActive && isLoading
                        ? stageMessage 
                        : thinkingState.finalStageMessage || ""
                    }
                    progress={thinkingState.isActive ? progress : thinkingState.finalProgress}
                    isExpanded={thinkingState.isExpanded}
                    onToggleExpanded={(expanded) => handleToggleExpanded(associatedThinkingId, expanded)}
                    requirementHistory={thinkingState.requirementHistory}
                    onUpdateRequirementHistory={thinkingState.isActive ? handleUpdateRequirementHistory : undefined}
                    trackedPlans={thinkingState.trackedPlans}
                    onUpdateTrackedPlans={thinkingState.isActive ? handleUpdateTrackedPlans : undefined}
                  />
                )}
              </>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default MessageList;
